import { useMemo } from 'react';
import { keepSelectedOption } from './keepSelectedOption';
import { useAutoFocus } from './useAutoFocus';
import { useBlurCommit } from './useBlurCommit';
import { useEscapeClosesPopupOnly } from './useEscapeClosesPopupOnly';
import { useLocalFilter } from './useLocalFilter';
import { useOptionKeys } from './useOptionKeys';
import { useSelectTopOnEnter } from './useSelectTopOnEnter';
import { useTriggerWidth } from './useTriggerWidth';

export interface AutocompleteControllerOptions<T extends object> {
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  value?: T | null;
  onChange?: (value: T | null) => void;
  isLoading: boolean;
  filterOptions: boolean;
  onInputChangeProp?: (value: string) => void;
  suggestedValue?: T;
  autoFocus?: boolean;
  selectOnEnter?: boolean;
  selectOnTab?: boolean;
}

export interface AutocompleteController<T extends object> {
  // Refs
  groupRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  // State / derived
  groupWidth: number | undefined;
  isFocused: boolean;
  isShowingSuggested: boolean | undefined;
  displayValue: string;
  filteredOptions: T[];
  listboxId: string;
  // Option helpers
  getKey: (option: T) => string;
  isOptionSelected: (option: T) => boolean;
  // Handlers
  handleSelectionChange: (key: React.Key | null) => void;
  handleInputChange: (newValue: string) => void;
  handleClear: () => void;
  onFocusChange: (focused: boolean) => void;
  handleKeyDownCapture: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function useAutocompleteController<T extends object>({
  options,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  value,
  onChange,
  isLoading,
  filterOptions,
  onInputChangeProp,
  suggestedValue,
  autoFocus,
  selectOnEnter,
  selectOnTab,
}: AutocompleteControllerOptions<T>): AutocompleteController<T> {
  const { groupRef, groupWidth } = useTriggerWidth();

  const { getKey, isOptionSelected } = useOptionKeys<T>({ value, getOptionLabel, getOptionKey, isOptionEqualToValue });
  const listedOptions = useMemo(() => keepSelectedOption(options, value, getKey), [options, value, getKey]);

  const {
    inputValue,
    isFocused,
    isSearching,
    inputRef,
    inputValueRef,
    pendingCommitRef,
    handleSelectionChange,
    handleInputChange,
    handleClear,
    onFocusChange,
  } = useBlurCommit<T>({
    value,
    onChange,
    options: listedOptions,
    getKey,
    getOptionLabel,
    suggestedValue,
    onInputChangeProp,
  });

  useAutoFocus(autoFocus, inputRef);

  const filteredOptions = useLocalFilter<T>({
    options: listedOptions,
    inputValue,
    getOptionLabel,
    filterOptions,
    isSearching,
  });

  const { listboxId, handleKeyDownCapture } = useSelectTopOnEnter<T>({
    selectOnEnter,
    selectOnTab,
    isLoading,
    filteredOptions,
    value,
    getOptionLabel,
    inputValueRef,
    queueCommit: (option) => {
      pendingCommitRef.current = option;
    },
  });

  const escapeKey = useEscapeClosesPopupOnly();

  // Track if we're showing the suggested value (no actual value selected)
  const isShowingSuggested = !value && suggestedValue && !isFocused;

  // Sync input value with selected value (or suggested value)
  const displayValue = useMemo(() => {
    if (isFocused) return inputValue;
    if (value) return getOptionLabel(value);
    if (suggestedValue) return getOptionLabel(suggestedValue);
    return '';
  }, [value, inputValue, isFocused, getOptionLabel, suggestedValue]);

  return {
    groupRef,
    inputRef,
    groupWidth,
    isFocused,
    isShowingSuggested,
    displayValue,
    filteredOptions,
    listboxId,
    getKey,
    isOptionSelected,
    handleSelectionChange,
    handleInputChange,
    handleClear,
    onFocusChange,
    handleKeyDownCapture: (e) => {
      escapeKey.onKeyDownCapture(e);
      handleKeyDownCapture(e);
    },
    handleKeyDown: escapeKey.onKeyDown,
  };
}
